/**
 * The frame (unit 26A, item 5; v1's `Shell.svelte`).
 *
 * **Every open widget is rendered once, keyed on its tile's id**, and placed by
 * CSS grid from its current index. A widget that moves — a swap, an expand of
 * the right tile, a fold — is the same component in a new place, so a draft in
 * it survives (v1.6, v1.82). The chat region beside them is the launcher or
 * the conversation, and the box (26B); the recording bar is at the foot.
 */
import { ChatRegion } from "../chat/ChatRegion";
import { IssuePopup } from "../chat/IssuePopup";
import { useChatLive } from "../chat/live";
import { RecordingBar } from "../recorder/RecordingBar";
import { Banners } from "./Banners";
import { useShellBoot } from "./boot";
import { layoutName } from "./layout";
import styles from "./Shell.module.css";
import { TileMount } from "./TileMount";
import { Tray } from "./Tray";
import { useLayout } from "./useLayout";

export function Shell() {
  useShellBoot();
  useChatLive();
  const layout = useLayout();
  const name = layoutName(layout);
  // Folded and Expanded both show one widget over chat; folded with none is Empty.
  const visual =
    name === "folded"
      ? layout.tiles[0]
        ? "single"
        : "empty"
      : name === "expanded"
        ? "single"
        : name;
  const panel = visual === "empty" || visual === "state1";
  const slots = layout.tiles.flatMap((tile, index) =>
    tile ? [{ tile, index: index as 0 | 1 }] : [],
  );

  return (
    <div className={styles.app}>
      <Banners />
      <div className={styles.grid} data-layout={visual} data-chat={layout.chatSlot ?? undefined}>
        {slots.map(({ tile, index }) => (
          <div key={tile.id} className={styles.slot} data-idx={index}>
            <TileMount id={tile.id} index={index} name={tile.widget} />
          </div>
        ))}
        <div
          className={panel ? `${styles.chat} ${styles.panel}` : `${styles.chat} ${styles.compact}`}
        >
          <ChatRegion panel={panel} />
        </div>
        {visual === "state2" && (
          <button
            className={styles.swap}
            onClick={layout.swap}
            aria-label="Swap left and right widgets"
            title="Swap left ↔ right"
          >
            <svg aria-hidden="true" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M3 4h7M8 2l2 2-2 2M11 10H4M6 8l-2 2 2 2"
                stroke="currentColor"
                strokeWidth={1.4}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
        <Tray />
      </div>
      <RecordingBar />
      <IssuePopup />
    </div>
  );
}
